/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { BaseQueryFn, FetchArgs, fetchBaseQuery } from '@reduxjs/toolkit/dist/query';
import { BaseQueryApi, QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import { IPagedResult } from '../models/filter';
import { getEnvSettings } from '../app/hostBasedSettings';
import { RootState } from '../redux/store';

export const prepareHeaders = (headers: Headers, { getState }: { getState(): unknown }): Headers => {
    const token = (getState() as RootState).authState.token || 'not_initialized';
    headers.set('authorization', `Bearer ${token}`);
    return headers;
};

export const createBaseQuery = (apiPath: string): BaseQueryFn => fetchBaseQuery({
    baseUrl: getEnvSettings().apiBaseUrl + apiPath,
    prepareHeaders,
});

export const createRbacBaseQuery = (apiPath: string): BaseQueryFn => fetchBaseQuery({
    baseUrl: getEnvSettings().rbacInterceptorUrl + apiPath,
    prepareHeaders,
});

export const createDirectoryBaseQuery = (apiPath: string): BaseQueryFn => fetchBaseQuery({
    baseUrl: getEnvSettings().directoryApiUrl + apiPath,
    prepareHeaders,
});

export const post = <T>(url: string, body: NonNullable<T>): FetchArgs => ({
    url,
    method: 'POST',
    body,
});

export const put = <T>(url: string, body: NonNullable<T>): FetchArgs => ({
    url,
    method: 'PUT',
    body,
});

export const patch = <T>(url: string, body?: NonNullable<T>): FetchArgs => ({
    url,
    method: 'PATCH',
    body,
});

export const del = <T>(url: string, body?: NonNullable<T>): FetchArgs => ({
    url,
    method: 'DELETE',
    body,
});

export function pagedResultToCacheTags<T1, T2>(pagedResult: IPagedResult<{ id: string | number }> | undefined, tag: T1, listTag: T2): Array<T1 | T2 | { type: T1, id: string | number } | { type: T2, id: string | number }> {
    if (listTag) {
        if (pagedResult)
            return [listTag, ...pagedResult.results.map(({ id }) => ({ type: tag, id }))];
        else
            return [listTag];
    }
    else {
        if (pagedResult)
            return [...pagedResult.results.map(({ id }) => ({ type: tag, id }))];
        else
            return [];
    }
}

export function listToCacheTags<T1, T2>(list: { id: string | number }[] | undefined, tag: T1, listTag?: T2): Array<T1 | T2 | { type: T1, id: string | number } | { type: T2, id: string | number }> {
    if (listTag) {
        if (list)
            return [listTag, ...list.map(({ id }) => ({ type: tag, id }))];
        else
            return [listTag];
    }
    else {
        if (list)
            return [...list.map(({ id }) => ({ type: tag, id }))];
        else
            return [];
    }
}

interface Cache404ResponseQueryFnParams<TArg, TData, TResponse = TData> {
    query(arg: TArg): string | FetchArgs;
    defaultValue: TData;
    transformResponse?(response: TResponse): TData | null;
}

const defaultTransformResponse = (response: any) => response;

export function cache404ResponseQueryFn<TArg, TData, TResponse = TData>({ query, defaultValue, transformResponse = defaultTransformResponse }: Cache404ResponseQueryFnParams<TArg, TData, TResponse>) {
    return async (arg: TArg, _: BaseQueryApi, __: {}, baseQuery: (arg: any) => MaybePromise<QueryReturnValue<unknown, unknown, {}>>) => {
        const result = await baseQuery(query(arg)) as QueryReturnValue<TResponse, { status: number }, unknown>;

        if (result.data !== undefined) { // null means 200/204 with no content
            const data = transformResponse(result.data);
            return { data: data ?? defaultValue, meta: result.meta };
        }

        if (result.error && result.error.status === 404)
            return { data: defaultValue, meta: result.meta };

        return { error: result.error, meta: result.meta };
    };
}

export function queryFnWithTenantIdArg<TArg extends Record<string, unknown> | void, TResponse>(query: (args: TArg & { tenantId: string }) => string | FetchArgs) {
    return async (arg: TArg, { getState }: BaseQueryApi, __: {}, baseQuery: (arg: any) => MaybePromise<QueryReturnValue<unknown, unknown, {}>>) => {
      const tenantId = (getState() as RootState).authState.tenantId;
  
      if (!tenantId)
        return { error: { name: 'ConditionError', message: 'The tenantId could not be recovered from store.' } };
  
      const result = await baseQuery(query({ ...arg, tenantId })) as QueryReturnValue<TResponse, { status: number }, unknown>;
  
      if (result.data !== undefined) { // null means 200/204 with no content
        return { data: result.data, meta: result.meta };
      }
  
      return { error: result.error, meta: result.meta };
    };
}

interface PatchAdd {
    op: 'add';
    path: string;
    value: any;
}

interface PatchRemove {
    op: 'remove';
    path: string;
}

interface PatchReplace {
    op: 'replace';
    path: string;
    value: any;
}

interface PatchMove {
    op: 'move';
    path: string;
    from: string;
}

interface PatchCopy {
    op: 'copy';
    path: string;
    from: string;
}

interface PatchTest {
    op: 'test';
    path: string;
    value: any;
}

export type JsonPatchData = (PatchAdd | PatchRemove | PatchReplace | PatchMove | PatchCopy | PatchTest)[];